import { useState } from 'react';
import type { FormEvent } from 'react';

const SUBMIT_ADDR = 'https://readdy.ai/api/form/db4h909rqll58fj4o89g';
const INTERESTS = ['Homepage feature', 'Tournament spotlight', 'Newsletter sponsorship', 'Event sponsorship', 'Not sure yet'];

type Status = 'idle' | 'sending' | 'success' | 'error';
type FormResponse = { code?: string; message?: string; meta?: { message?: string; detail?: string } };

const fieldClass = 'w-full text-sm border border-background-300 rounded-md px-4 py-3 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block font-label uppercase tracking-widest text-xs text-foreground-600 mb-1.5';

export default function AdvertiseForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    if (String(data.get('website_alt') || '').trim()) {
      setStatus('success');
      setMessage('Thanks — your enquiry is on its way. We will be in touch shortly.');
      form.reset();
      return;
    }

    const email = String(data.get('email') || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus('error');
      setMessage('Please enter a valid email address so we can reach you.');
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === 'website_alt') return;
      const v = String(value).trim();
      if (v) payload.append(key, v);
    });

    setStatus('sending');
    setMessage('');
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setMessage('Thanks — your enquiry is on its way. We will be in touch shortly.');
        form.reset();
      } else {
        setStatus('error');
        setMessage(typeof serverMsg === 'string' && serverMsg ? serverMsg : 'Something went wrong. Please try again.');
      }
    } catch {
      setStatus('error');
      setMessage('Network error. Please check your connection and try again.');
    }
  };

  return (
    <section id="advertise" className="bg-foreground-950 px-4 md:px-8 py-16 md:py-20">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="font-label uppercase tracking-[0.25em] text-sm text-accent-400 font-bold">Advertise with us</p>
          <h2 className="font-heading font-extrabold uppercase text-4xl md:text-6xl text-background-50 leading-none mt-2">Let&apos;s build your package</h2>
          <p className="mt-4 text-background-200 max-w-md">
            Tell us about your business and what you&apos;d like to achieve. We&apos;ll come back with a tailored sponsorship proposal and available dates.
          </p>
          <ul className="mt-6 space-y-3 text-background-200 text-sm">
            {['Mix and match placements across the site', 'Barter welcome — rounds, F&B credits or merchandise', 'We reply to every enquiry personally'].map((line) => (
              <li key={line} className="flex items-start gap-3">
                <span className="mt-0.5 w-5 h-5 flex items-center justify-center text-accent-400"><i className="ri-check-line"></i></span>{line}
              </li>
            ))}
          </ul>
          <a href="tel:5622432854" className="mt-7 inline-flex items-center gap-2 font-heading font-bold text-xl text-accent-400 cursor-pointer">
            <span className="w-6 h-6 flex items-center justify-center"><i className="ri-phone-line"></i></span>562-243-2854
          </a>
        </div>

        <div className="lg:col-span-7">
          <form data-readdy-form id="advertise-form" onSubmit={handleSubmit} className="bg-background-50 rounded-lg p-5 md:p-7 border-t-4 border-accent-500">
            <input type="text" name="website_alt" className="field-trap" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="company" className={labelClass}>Business name</label>
                <input id="company" name="company" type="text" required placeholder="e.g. Coastal Mutual" className={fieldClass} />
              </div>
              <div>
                <label htmlFor="name" className={labelClass}>Your name</label>
                <input id="name" name="name" type="text" required placeholder="First and last name" className={fieldClass} />
              </div>
              <div>
                <label htmlFor="email" className={labelClass}>Email</label>
                <input id="email" name="email" type="email" required placeholder="you@company.com" className={fieldClass} />
              </div>
              <div>
                <label htmlFor="phone" className={labelClass}>Phone <span className="normal-case tracking-normal text-foreground-400">(optional)</span></label>
                <input id="phone" name="phone" type="tel" placeholder="562-000-0000" className={fieldClass} />
              </div>
              <div className="sm:col-span-2">
                <span className={labelClass}>What interests you?</span>
                <div className="flex flex-wrap gap-2">
                  {INTERESTS.map((i) => (
                    <label key={i} className="inline-flex items-center gap-2 border border-background-300 rounded-full px-3.5 py-2 text-sm cursor-pointer hover:border-foreground-700 has-[:checked]:bg-foreground-950 has-[:checked]:text-background-50 has-[:checked]:border-foreground-950 transition-colors">
                      <input type="checkbox" name="interest" value={i} className="accent-primary-500" />
                      {i}
                    </label>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="message" className={labelClass}>Message <span className="normal-case tracking-normal text-foreground-400">(optional)</span></label>
                <textarea id="message" name="message" rows={4} maxLength={500} placeholder="Goals, budget range, timing — up to 500 characters." className={`${fieldClass} resize-none`}></textarea>
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
              <button type="submit" disabled={status === 'sending'} className="bg-primary-500 hover:bg-primary-600 text-background-50 font-label font-bold uppercase tracking-wider px-6 py-3 rounded-md whitespace-nowrap cursor-pointer disabled:opacity-60">
                {status === 'sending' ? 'Sending...' : 'Send enquiry'}
              </button>
              <p className="text-xs text-foreground-500">We only use your details to reply about sponsorship.</p>
            </div>

            {status === 'success' && <p className="mt-4 text-sm text-primary-600 font-medium">{message}</p>}
            {status === 'error' && <p className="mt-4 text-sm text-secondary-700 font-medium">{message}</p>}
          </form>
        </div>
      </div>
    </section>
  );
}